/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import {
    takeEvery, call, fork, select, put,
} from 'redux-saga/effects';
import mbpLogger from 'mbp-logger';
import mbpUtil from 'mbp-api-util';

import { getIsWriteScopeCheckout } from '../../../../Checkout/ducks/Global/Global-Selectors';
import addressBookServiceClient from '../../../../../../apis/address-book-apis/addressBookServiceClient';
import {
    addAddressBookEntry, clearAddressBookEntry, editAddressBookEntry,
    loadAddressBookEntry, loadBillingAddressBookEntry, proccessAddressBookEntryCompleted,
    removeAddressBookEntry, savedAddressBookEntry, setAddressBookEntry,
    setBillingUserAddressBookEntry,
} from './AddressBookEntry-Actions';
import { processFeatureErrors } from '../../../../Checkout/ducks/Common/ducks/Error/Error-Operations';
import { getRegisterAddressBookPageNumber } from './AddressBookEntry-Selector';
import { getFeatureFlags } from '../../App/App-Selectors';
import { updateChatgptGiftMessage } from '../../CartItem/ChatGptMessage/ChatGptMessage-Actions';
import { checkJWT } from '../../../../Checkout/ducks/Global/Global-Operations';
import { flagOffShowLoadingMessage, updateShowLoadingMessage } from '../../../../Common/ducks/ShowLoadingMessage/ShowLoadingMessage-Actions';
import { getContactId } from '../../../../Member/ducks/Common/Common-Selectors';
import { getBrandConfig } from '../../../../Checkout/ducks/App/App-Selectors';

function* workerLoadAddressBookEntry(action) {
    const contactId = yield select(getContactId);
    try {
        let totalEntriesAddress = 0;
        const isWriteScopeCheckout = yield select(getIsWriteScopeCheckout);

        if (contactId && isWriteScopeCheckout) {
            const jwtToken = yield call(checkJWT);

            const payload = {
                wcEnv: {},
                contactId,
                jwtToken,
            };
            const response = yield call(addressBookServiceClient.getAllAddressBookV2, payload);

            if (response.data && response.data.addressBookResponse) {
                yield put(clearAddressBookEntry());
                const { addressList, totalEntries } = response.data.addressBookResponse;
                const totalPages = 1; // Math.ceil(totalEntries / TOTAL_ENTRIES_PER_PAGE);
                totalEntriesAddress = totalEntries;
                const addressBookList = [];
                addressList.forEach((addressBookItem) => {
                    const addressesGroupByName = addressBookItem.addresses || [];
                    addressesGroupByName.forEach((addressBookRec) => {
                        if (addressBookRec.addressLineOne) {
                            addressBookList.push({
                                ...addressBookItem,
                                addresses: [{ ...addressBookRec }],
                            });
                        }
                    });
                });
                yield put(setAddressBookEntry(addressBookList, totalEntries, 0, totalPages));
            } else {
                yield put(clearAddressBookEntry());
                yield put(savedAddressBookEntry());
            }
        }
        if (action.data.handleCallback) {
            yield call(action.data.handleCallback, false, totalEntriesAddress);
        }
        yield put(proccessAddressBookEntryCompleted());
    } catch (ex) {
        const { response } = ex;
        if (
            response
            && response.data
            && response.data.StatusCode
            && response.data.StatusCode.toString() !== '404'
        ) {
            mbpLogger.logError({
                function: 'workerLoadAddressBookEntry',
                appName: process.env.npm_package_name,
                module: 'mbp-checkout',
                jsError: ex,
                message: `workerLoadAddressBookEntry - Load Address Book Failed - contactId:${contactId}`,
            });
        }
        yield put(setAddressBookEntry([], 0, 0, 0));
        if (action.data.handleCallback) {
            yield call(action.data.handleCallback, false, 0);
        }
        yield put(proccessAddressBookEntryCompleted());
    }
}

export function* workerEditAddressBookEntry(action) {
    const contactId = yield select(getContactId);

    try {
        const { data } = action;
        const jwtToken = yield call(checkJWT);
        const brandConfig = yield select(getBrandConfig);

        const addressBookEntry = {
            ...data,
            addressType: data.addressType || 'Residence',
            brandId: brandConfig.id,
        };

        if (data.addressType) {
            addressBookEntry.addressType = data.addressType;
        }

        const payload = {
            wcEnv: {},
            jwtToken,
            contactId,
            addressBookEntry,
            addressBookEntryId: data.addressBookEntryId,
        };

        const response = yield call(addressBookServiceClient.updateAddressBookV2, payload);
        if (response && response.data && response.data.status === 200) {
            const pageNumber = yield select(getRegisterAddressBookPageNumber);
            const info = {
                data: {
                    pageNumber,
                },
            };
            yield call(workerLoadAddressBookEntry, info);
        }
        yield put(proccessAddressBookEntryCompleted());
    } catch (ex) {
        mbpLogger.logError({
            action,
            function: 'workerEditAddressBookEntry',
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            jsError: ex,
            message: `workerEditAddressBookEntry - Edit Address Book Failed - ${contactId}`,
        });

        yield fork(processFeatureErrors, {
            data: {
                orderItemId: action.data.orderItemId,
                errorFeatureType: 'recipient',
                errorValues: ex.response?.data,
            },
        });
    }
}

export function* workerAddAddressBookEntry(action) {
    const contactId = yield select(getContactId);
    try {
        const {
            data, handleCallBack, recipientFormDetails, cartItemId,
        } = action;
        const featureFlags = yield select(getFeatureFlags);
        const jwtToken = yield call(checkJWT);
        const brandConfig = yield select(getBrandConfig);

        const payload = {
            wcEnv: {},
            jwtToken,
            contactId,
            addressBookEntry: {
                ...data,
                brandId: brandConfig.id,
            },
        };

        const response = yield call(addressBookServiceClient.addAddressBookV2, payload);
        if (response && response.data && [200, 201].includes(response.status)) {
            if (handleCallBack && recipientFormDetails) {
                const recipientData = {
                    ...recipientFormDetails,
                    entryId: response.data.addressResponse.addressBookEntryId,
                    addressId: response.data.addressResponse.addressBookEntryId,
                    addressBookRecipientId: response.data.addressResponse.rcpId,
                };

                if (featureFlags['is-chat-gpt-enabled']) {
                    yield call(updateChatgptGiftMessage, cartItemId, { recipientName: recipientData?.firstName, relation: recipientData?.relationship });
                }
                yield call(handleCallBack, false, recipientData, 'addressBook');
            }
        }

        yield put(proccessAddressBookEntryCompleted());
    } catch (ex) {
        yield fork(processFeatureErrors, {
            data: {
                orderItemId: action.data.orderItemId,
                errorFeatureType: 'recipient',
                errorValues: ex.response?.data,
            },
        });

        mbpLogger.logError({
            action,
            function: 'workerEditAddressBookEntry',
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            jsError: ex,
            message: `workerEditAddressBookEntry - Add Address Book Failed ${contactId}`,
        });

        yield put(proccessAddressBookEntryCompleted());
    }
}

function* workerRemoveAddressBookEntry(action) {
    const contactId = yield select(getContactId);
    try {
        const { data } = action;
        const jwtToken = yield call(checkJWT);

        const payload = {
            wcEnv: {},
            jwtToken,
            contactId,
            addressBookEntryId: data.addressBookEntryId,
        };

        yield put(updateShowLoadingMessage());
        // const response = yield call(personClient.removeAddressBook, wcEnv, contactId, data.addressBookEntryId, accessToken);
        const response = yield call(addressBookServiceClient.removeAddressBookV2, payload);

        if (response.status === 200) {
            const pageNumber = yield select(getRegisterAddressBookPageNumber);
            const info = {
                data: {
                    pageNumber,
                },
            };
            yield call(workerLoadAddressBookEntry, info);
        }
    } catch (ex) {
        mbpLogger.logError({
            action,
            function: 'workerRemoveAddressBookEntry',
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            jsError: ex,
            message: `workerRemoveAddressBookEntry - Remove Address Book Failed - contactId: ${contactId}`,
        });
    }

    yield put(flagOffShowLoadingMessage());
}

function* workerLoadBillingAddressBookEntry() {
    const contactId = yield select(getContactId);
    try {
        const jwtToken = yield call(checkJWT);
        const wcEnv = {
            host: mbpUtil.getEnv('APP_WC_HOST'),
        };

        const payload = {
            wcEnv,
            contactId,
            jwtToken,
        };

        const response = yield call(addressBookServiceClient.getBillingUserAddressBook, payload);
        if (response?.data?.addressBookResponse?.addresses?.[0]) {
            yield put(setBillingUserAddressBookEntry(response.data.addressBookResponse.addresses[0]));
        } else {
            yield put(setBillingUserAddressBookEntry({}));
        }
    } catch (ex) {
        const { response } = ex;
        mbpLogger.logError({
            function: 'workerLoadBillingAddressBookEntry',
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            jsError: ex,
            message: `workerLoadBillingAddressBookEntry - Load Billing Address Failed - contactId: ${contactId}`,
        });
    }
}

function* watcherLoadAddressBookEntry() {
    yield takeEvery(loadAddressBookEntry().type, workerLoadAddressBookEntry);
}

function* watcherLoadBillingAddressBookEntry() {
    yield takeEvery(loadBillingAddressBookEntry().type, workerLoadBillingAddressBookEntry);
}

function* watcherAddAddressBookEntry() {
    yield takeEvery(addAddressBookEntry().type, workerAddAddressBookEntry);
}

function* watcherEditAddressBookEntry() {
    yield takeEvery(editAddressBookEntry().type, workerEditAddressBookEntry);
}

function* watcherRemoveAddressBookEntry() {
    yield takeEvery(removeAddressBookEntry().type, workerRemoveAddressBookEntry);
}

const watchers = [
    fork(watcherLoadAddressBookEntry),
    fork(watcherLoadBillingAddressBookEntry),
    fork(watcherAddAddressBookEntry),
    fork(watcherEditAddressBookEntry),
    fork(watcherRemoveAddressBookEntry),
];

// export default {
//     watchers,
//     workers,
// };

export { watchers };

export default {};
